import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

function CalendarDaySelect({ onDaySelect, onCloseCalendar, selectedMonth }) {
    const currentDate = new Date();
    const [currentDateState, setCurrentDateState] = useState(selectedMonth || currentDate);
    const [selectedDay, setSelectedDay] = useState(null);

    useEffect(() => {
        setCurrentDateState(selectedMonth || currentDate);
    }, [selectedMonth]);

    const calculateDaysInMonth = (year, month) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1);
        const startingDayOfWeek = firstDayOfMonth.getDay();
        const days = [];
        const emptyCells = (startingDayOfWeek + 6) % 7;
    
        const prevMonthDays = new Date(year, month, 0).getDate();
        for (let i = prevMonthDays - emptyCells + 1; i <= prevMonthDays; i++) {
            const date = new Date(year, month - 1, i);
            days.push({ date, isCurrentMonth: false });
        }
    
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            days.push({ date, isCurrentMonth: true });
        }
    
        const nextMonthDays = (7 - (days.length % 7)) % 7;
        for (let day = 1; day <= nextMonthDays; day++) {
            const date = new Date(year, month + 1, day);
            days.push({ date, isCurrentMonth: false });
        }
    
        return days;
    };   

    const previousMonth = () => {
        const newDate = new Date(currentDateState);
        newDate.setMonth(newDate.getMonth() - 1);
        setCurrentDateState(newDate);
    };

    const nextMonth = () => {
        const newDate = new Date(currentDateState);
        newDate.setMonth(newDate.getMonth() + 1);
        setCurrentDateState(newDate);
    };

    const previousYear = () => {
        const newDate = new Date(currentDateState);
        newDate.setFullYear(newDate.getFullYear() - 1);
        setCurrentDateState(newDate);
    };
    
    const nextYear = () => {
        const newDate = new Date(currentDateState);
        newDate.setFullYear(newDate.getFullYear() + 1);
        setCurrentDateState(newDate);
    };

    const handleDaySelect = (date) => {
        onDaySelect(date);
        onCloseCalendar();
    };

    const handleDayMouseEnter = (index) => {
        setSelectedDay(index);
    };

    const handleDayMouseLeave = () => {
        setSelectedDay(null);
    };

    const days = calculateDaysInMonth(currentDateState.getFullYear(), currentDateState.getMonth());
    const weekDays = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

    return (
        <div className="grid grid-cols border-y border-gray-200 py-2 divide-x divide-gray-200">
            <div className="px-5">
                <div className="grid grid-cols-5">
                    <div className='col-span-1 mt-2'>
                        {currentDateState.getMonth() === new Date().getMonth() && currentDateState.getFullYear() === new Date().getFullYear() ? null : (
                            <button
                                type="button"
                                onClick={() => setCurrentDateState(new Date())}
                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 shadow-sm uppercase"
                            >
                                Torna al mese in corso
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={previousYear}
                        className="-my-1.5 flex items-center p-1.5 text-gray-400 hover:text-gray-500 col-span-1 justify-end"
                    >
                        <span className="sr-only">Previous Year</span>
                        <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <div className="col-span-1 flex items-center justify-center">
                        <h2 className="text-sm font-semibold text-gray-700">
                            {currentDateState.getFullYear()}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={nextYear}
                        className="my-1.5 mr-1.5 ml-2 flex items-center p-1.5 text-gray-400 hover:text-gray-500 col-span-1 justify-start"
                    >
                        <span className="sr-only">Next year</span>
                        <ChevronRightIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="grid grid-cols-5">
                    <div className='col-span-1 mt-2'></div>
                    <button
                        type="button"
                        onClick={previousMonth}
                        className="-my-1.5 flex items-center p-1.5 text-gray-400 hover:text-gray-500 col-span-1 justify-end"
                    >
                        <span className="sr-only">Previous month</span>
                        <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <div className="col-span-1 flex items-center justify-center">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase">
                            {currentDateState.toLocaleString('default', { month: 'long' })} {currentDateState.getFullYear()}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={nextMonth}
                        className="my-1.5 mr-1.5 ml-2 flex items-center p-1.5 text-gray-400 hover:text-gray-500 col-span-1 justify-start"
                    >
                        <span className="sr-only">Next month</span>
                        <ChevronRightIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="mt-2 grid grid-cols-7 text-center text-xs leading-6 text-gray-500">
                    {(weekDays).map((day, index) => (
                        <div key={index} className="py-1">
                            {day}
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-7 text-sm">
                    {days.map(({ date, isCurrentMonth }, dayIdx) => {
                        return (
                            <div
                                key={dayIdx}
                                className={classNames(
                                    selectedDay === dayIdx ? 'bg-indigo-50' : '',
                                    !isCurrentMonth ? 'text-gray-400' : '', 
                                    'border-t border-gray-200 py-2'
                                )}
                                onMouseEnter={() => handleDayMouseEnter(dayIdx)}
                                onMouseLeave={handleDayMouseLeave}
                            >
                                <button
                                    type="button"
                                    className="w-full h-full"
                                    onClick={() => handleDaySelect(date)}
                                    disabled={!isCurrentMonth}
                                >
                                    {date.getDate()}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default CalendarDaySelect;
